import React, { useEffect, useMemo, useState } from "react";
import * as d3 from "d3";
import { Modes } from "./Standing";
import { getSizeScaledFromFullHD, getStyleVariable } from "./UtilityFunctions";
import {
  animate,
  AnimatePresence,
  motion,
  MotionConfig,
  MotionValue,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from "framer-motion";

interface VoteData {
  name: string;
  value: number;
}

export default function BarChartD3Framer({
  data = [],
  mode = Modes.PreAscending,
  quota,
  showLabels = true,
  width = getSizeScaledFromFullHD(1240),
  height = getSizeScaledFromFullHD(800),
  marginTop = getSizeScaledFromFullHD(20),
  marginRight = getSizeScaledFromFullHD(20),
  marginBottom = getSizeScaledFromFullHD(50),
  marginLeft = getSizeScaledFromFullHD(80),
  curtainReveal = false,
}: {
  data: VoteData[];
  mode?: Modes;
  quota?: number;
  showLabels?: boolean;
  width?: number;
  height?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  curtainReveal?: boolean;
}) {
  const MINIMUM_HEIGHT = 200;
  const clipPathCurtainUp = mode === Modes.Ascending;
  const clipPathActive = mode === Modes.Ascending && curtainReveal;
  const CURTAIN_RISE_ANIMATION_DURATION = 5;
  const AXIS_ANIMATION_DURATION = clipPathActive
    ? CURTAIN_RISE_ANIMATION_DURATION
    : 1;

  const headroom = 1.1;
  const dataWithHeadroom = data.map((d) => d.value * headroom);
  const calculatedMaxYScale = Math.max(
    ...dataWithHeadroom,
    MINIMUM_HEIGHT,
    (quota && quota * headroom) || 0
  );

  const maxYScaleMV = useMotionValue(calculatedMaxYScale);
  // const maxYScaleMV = useMotionValue(200);
  const [maxYScale, setMaxYScale] = useState(maxYScaleMV.get());

  useMotionValueEvent(maxYScaleMV, "change", (latest) => {
    setMaxYScale(latest);
  });

  const animateYAxis = (value: number) =>
    animate(maxYScaleMV, value, {
      duration: AXIS_ANIMATION_DURATION,
      type: "tween",
      ease: [0.56, 0, 0.44, 1],
    });

  useEffect(() => {
    animateYAxis(calculatedMaxYScale);
  }, [data, quota, calculatedMaxYScale]);

  const y = d3
    .scaleLinear()
    .range([height - marginBottom, marginTop])
    .domain([0, maxYScale]);

  const x = d3
    .scaleBand()
    .domain(data.map((d) => d.name))
    .range([marginLeft, width - marginRight])
    .padding(0.2);

  const FADE_HEIGHT = getSizeScaledFromFullHD(20);

  const [curtainStoppedMoving, setCurtainStoppedMoving] = useState(true);

  const highestDataValue = useMemo(
    () => Math.max(...data.map((d) => d.value)),
    [data]
  );

  const clipPathCurtainHeightMV = useMotionValue(
    clipPathCurtainUp ? height - marginBottom - marginTop : 0
  );

  const clipPathCurtainYMV = useMotionValue(
    clipPathCurtainUp ? marginTop : y(0)
  );

  useEffect(() => {
    const aStartUnsub = clipPathCurtainHeightMV.on("animationStart", () =>
      setCurtainStoppedMoving(false)
    );
    const aEndUnsub = clipPathCurtainHeightMV.on("animationComplete", () =>
      setCurtainStoppedMoving(true)
    );
    return () => {
      aStartUnsub();
      aEndUnsub();
    };
  }, []);

  useEffect(() => {
    const finalYScaleState = d3
      .scaleLinear()
      .range([height - marginBottom, marginTop])
      .domain([0, calculatedMaxYScale]); //curtain only cares about the final largest y-value value as that will be the greatest

    const newHeight = clipPathCurtainUp
      ? finalYScaleState(0) - finalYScaleState(highestDataValue)
      : 0;
    const newY = clipPathCurtainUp
      ? finalYScaleState(highestDataValue)
      : finalYScaleState(0);
    if (clipPathActive) {
      animate(clipPathCurtainHeightMV, newHeight, {
        duration: CURTAIN_RISE_ANIMATION_DURATION,
        type: "tween",
        ease: "easeInOut",
      });
      animate(clipPathCurtainYMV, newY, {
        duration: CURTAIN_RISE_ANIMATION_DURATION,
        type: "tween",
        ease: "easeInOut",
      });
    } else {
      clipPathCurtainHeightMV.jump(newHeight);
      clipPathCurtainYMV.jump(newY);
    }
  }, [
    clipPathCurtainUp,
    clipPathActive,
    calculatedMaxYScale,
    highestDataValue,
  ]);

  return (
    <MotionConfig transition={{ type: "tween" }}>
      <svg width={width} height={height}>
        <defs>
          <motion.clipPath
            id="cut-bottom"
            transition={{ duration: 1 }}
            initial={false}
            animate={{
              // this is annoying and weird, but it's because of the slightly backwards line positioning method explained in the comments of the Bar component
              x: -x.bandwidth() / 2,
            }}
          >
            <motion.rect
              width={width - marginRight - marginLeft}
              y={clipPathCurtainYMV}
              height={clipPathCurtainHeightMV}
            />
          </motion.clipPath>
          <linearGradient id="fade-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#000000" />
            <stop offset="100%" stopColor="#ffffff" />
          </linearGradient>
          <mask id="fade-top">
            <rect
              x="0"
              y="0"
              width={width}
              height={FADE_HEIGHT}
              fill="url(#fade-gradient)"
            />
            <rect
              x={0}
              y={FADE_HEIGHT}
              width={width}
              height={height - FADE_HEIGHT}
              fill={"white"}
            />
          </mask>
        </defs>
        <Axis
          scale={y}
          width={width}
          marginLeft={marginLeft}
          marginRight={marginRight}
          showTicks={false}
          showAxis={false}
          showGridLines={true}
        />
        <g id={"chart-bars"}>
          <AnimatePresence>
            {data.map((d) => (
              <Bar
                key={d.name}
                data={d}
                y={y}
                x={x}
                enableAnimation={!clipPathActive}
                enableClipPath={clipPathActive}
                showLabels={
                  mode === Modes.Ascending &&
                  showLabels &&
                  (clipPathActive
                    ? curtainStoppedMoving && clipPathCurtainUp
                    : true)
                }
                marginBottom={marginBottom}
              />
            ))}
          </AnimatePresence>
        </g>
        {quota && (
          <QuotaLine
            quota={quota}
            y={y}
            xOffset={marginLeft}
            xEnd={width - marginRight}
          />
        )}
      </svg>
    </MotionConfig>
  );
}

const Bar = ({
  data,
  y,
  x,
  enableAnimation = true,
  enableClipPath = false,
  showLabels = false,
  marginBottom,
}: {
  data: { value: number; name: string };
  y: d3.ScaleLinear<number, number, never>;
  x: d3.ScaleBand<string>;
  enableAnimation?: boolean;
  enableClipPath?: boolean;
  showLabels?: boolean;
  marginBottom: number;
}) => {
  const BAR_ANIMATION_DURATION = 1;

  const valueMV = useMotionValue(data.value);

  const [enableLabelVisibility, setEnableLabelVisibility] = useState(false);

  useEffect(() => {
    const aStartUnsub = valueMV.on("animationStart", () =>
      setEnableLabelVisibility(false)
    );
    const aEndUnsub = valueMV.on("animationComplete", () =>
      setEnableLabelVisibility(true)
    );
    return () => {
      aStartUnsub();
      aEndUnsub();
    };
  }, []);

  useEffect(() => {
    animate(valueMV, data.value, {
      duration: enableAnimation ? BAR_ANIMATION_DURATION : 0,
      type: "tween",
      ease: "easeInOut",
    });
  }, [data]);

  const yValueMV = useTransform(valueMV, (value) => y(value));
  const yValueWithHeadroomMV = useTransform(
    valueMV,
    (value) => y(value) - getSizeScaledFromFullHD(10)
  );
  const heightValueMV = useTransform(valueMV, (value) => y(0) - y(value));

  return (
    <motion.g
      initial={false}
      animate={{
        x: Number(x(data.name)) + x.bandwidth() / 2,
      }}
      transition={{ duration: 1 }}
    >
      <motion.rect
        height={heightValueMV}
        y={yValueMV}
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          width: x.bandwidth(),
          // this is a bit backwards, but it lets the group el center the text el even when the text is animating out
          attrX: -x.bandwidth() / 2,
        }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1 }}
        fill={getStyleVariable("--secondary")}
        clipPath={enableClipPath ? "url(#cut-bottom)" : undefined}
      />
      <AnimatePresence>
        {enableLabelVisibility && showLabels && (
          <motion.text
            textAnchor={"middle"}
            fill={getStyleVariable("--primary")}
            y={yValueWithHeadroomMV}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            {data.value}
          </motion.text>
        )}
      </AnimatePresence>
      <motion.text
        fontWeight={700}
        textAnchor={"middle"}
        fill={getStyleVariable("--primary")}
        y={y(0) + marginBottom / 2}
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
        }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1 }}
      >
        {data.name}
      </motion.text>
    </motion.g>
  );
};

const Axis = ({
  scale,
  width,
  marginLeft,
  marginRight,
  showTicks = true,
  showAxis = true,
  moreTicks = false,
  showGridLines = false,
}: {
  scale: d3.ScaleLinear<number, number, never>;
  width: number;
  marginLeft: number;
  marginRight: number;
  showTicks?: boolean;
  showAxis?: boolean;
  moreTicks?: boolean;
  showGridLines?: boolean;
}) => {
  const height = scale.range()[0] - scale.range()[1];
  const pixelsPerTick = useMemo(() => {
    return getSizeScaledFromFullHD(moreTicks ? 80 : 40);
  }, [moreTicks]);
  const numberOfTicksTarget = useMemo(() => {
    return Math.max(1, Math.floor(height / pixelsPerTick));
  }, [height, pixelsPerTick]);
  const ticks = scale.ticks(numberOfTicksTarget);
  return (
    <g id={"axis"}>
      {showAxis && (
        <line
          stroke={"currentColor"}
          y1={scale.range()[0]}
          y2={scale.range()[1]}
          x1={marginLeft}
          x2={marginLeft}
        />
      )}
      <g id={"ticks"} mask={"url(#fade-top)"}>
        <AnimatePresence custom={scale}>
          {ticks.map((value) => (
            <Tick
              key={value}
              value={value}
              scale={scale}
              width={width}
              marginLeft={marginLeft}
              marginRight={marginRight}
              showTicks={showTicks}
              showGridLines={showGridLines}
            />
          ))}
        </AnimatePresence>
      </g>
    </g>
  );
};

const Tick = ({
  value,
  scale,
  width,
  marginLeft,
  marginRight,
  showTicks = false,
  showGridLines = false,
}: {
  value: number;
  scale: d3.ScaleLinear<number, number, never>;
  width: number;
  marginLeft: number;
  marginRight: number;
  showTicks?: boolean;
  showGridLines?: boolean;
}) => {
  const TICK_WIDTH = getSizeScaledFromFullHD(10);
  const TICK_PADDING = getSizeScaledFromFullHD(4);

  const variants = {
    enter: (customScale: d3.ScaleLinear<number, number, never>) => ({
      opacity: 0,
      y: customScale(value),
    }),
    animate: {
      opacity: 1,
      y: scale(value),
    },
    exit: (customScale: d3.ScaleLinear<number, number, never>) => ({
      opacity: 0,
      y: customScale(value),
    }),
  };

  return (
    <motion.g
      key={value}
      custom={scale}
      transition={{
        y: { type: "tween", duration: 0 },
        opacity: { duration: 0.15 },
      }}
      variants={variants}
      initial={"enter"}
      animate={"animate"}
      exit={"exit"}
      id={"tick-" + value}
    >
      {showTicks && (
        <line
          x1={marginLeft - TICK_WIDTH}
          x2={marginLeft}
          stroke="currentColor"
        />
      )}
      <text
        // key={value}
        alignmentBaseline={"central"}
        style={{
          fontSize: `${getSizeScaledFromFullHD(20)}px`,
          textAnchor: "end",
          transform: `translateX(${
            marginLeft - ((showTicks ? TICK_WIDTH : 0) + TICK_PADDING)
          }px)`,
          fill: getStyleVariable("--primary"),
        }}
      >
        {value}
      </text>
      {showGridLines && (
        <line
          opacity={0.4}
          x1={marginLeft}
          x2={width - marginRight}
          stroke="currentColor"
        />
      )}
    </motion.g>
  );
};

const QuotaLine = ({
  quota,
  y,
  xOffset,
  xEnd,
  enableAnimation = true,
}: {
  quota: number;
  y: d3.ScaleLinear<number, number, never>;
  xOffset: number;
  xEnd: number;
  enableAnimation?: boolean;
}) => {
  const QUOTA_ANIMATION_DURATION = 1;

  const valueMV = useMotionValue(Number(quota));

  useEffect(() => {
    // christ knows why, but you have to cast the quota to a number here...even though it's already a number.
    animate(valueMV, Number(quota), {
      duration: enableAnimation ? QUOTA_ANIMATION_DURATION : 0,
      type: "tween",
      ease: "easeInOut",
    });
  }, [quota]);

  const yValueMV = useTransform(valueMV, (value) => y(value));
  const textYValueMV = useTransform(
    yValueMV,
    (value) => value - getSizeScaledFromFullHD(16)
  );

  const ENTRANCE_DELAY = 0.5;

  return (
    <g id={"quota-line"}>
      <motion.text
        x={xEnd}
        y={textYValueMV}
        textAnchor={"end"}
        stroke={getStyleVariable("--primary")}
        fill={getStyleVariable("--primary")}
        alignmentBaseline={"central"}
        fontSize={getSizeScaledFromFullHD(20)}
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: {
            delay: ENTRANCE_DELAY + QUOTA_ANIMATION_DURATION,
            duration: QUOTA_ANIMATION_DURATION,
          },
        }}
        exit={{ opacity: 0 }}
      >
        Quota
      </motion.text>
      <motion.line
        initial={{ x2: xOffset }}
        animate={{
          transition: {
            delay: ENTRANCE_DELAY,
            duration: QUOTA_ANIMATION_DURATION,
          },
          x1: xOffset,
          x2: xEnd,
        }}
        exit={{ x2: xOffset }}
        y1={yValueMV}
        y2={yValueMV}
        stroke={getStyleVariable("--secondary")}
        strokeWidth={getSizeScaledFromFullHD(5)}
        strokeDasharray={getSizeScaledFromFullHD(20)}
      />
    </g>
  );
};

type MotionValueState = [number | string, MotionValue];

export function useMotionValueState(
  initialValue: number | string
): MotionValueState {
  const value = useMotionValue(initialValue);
  const [valueState, setValueState] = useState(value.get());

  useEffect(() => value.on("change", setValueState), []);

  return [valueState, value];
}
